import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { setAuthToken } from "../services/api.service";
import { useNavigate } from "react-router-dom";
import { useProjectStore } from "../stores/project.store";

interface AuthContextType {
  auth: { accessToken: string; user: string } | null;
  setAuth: (auth: { accessToken: string; user: string } | null) => void;
  login: (auth: { accessToken: string; user: string }) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const AUTH_KEY = "auth";

  const [auth, setAuth] = useState<{
    accessToken: string;
    user: string;
  } | null>(() => {
    const stored = localStorage.getItem(AUTH_KEY);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      return typeof parsed?.accessToken === "string" && parsed.accessToken
        ? parsed
        : null;
    } catch {
      localStorage.removeItem(AUTH_KEY);
      return null;
    }
  });

  const isAuthenticated = !!auth?.accessToken;

  const login = useCallback(
    (authData: { accessToken: string; user: string }) => {
      localStorage.removeItem("project-storage");
      useProjectStore.getState().resetForUser();
      setAuth(authData);
      localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
      setAuthToken(authData.accessToken);
    },
    [],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("project-storage");
    useProjectStore.getState().resetForUser();
    setAuth(null);
    localStorage.removeItem(AUTH_KEY);
    setAuthToken(null);
    if (window.location.pathname !== "/auth") {
      navigate("/auth", { replace: true });
    }
  }, [navigate]);

  // Persist auth to localStorage
  useEffect(() => {
    if (auth) {
      localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
      setAuthToken(auth.accessToken);
    } else {
      localStorage.removeItem(AUTH_KEY);
      setAuthToken(null);
    }
  }, [auth]);

  // Auto logout on token expiration
  useEffect(() => {
    if (!auth?.accessToken) return;

    let intervalId: NodeJS.Timeout;

    try {
      const payload = JSON.parse(atob(auth.accessToken.split(".")[1]));
      const expiry = payload.exp * 1000;

      const checkExpiry = () => {
        if (Date.now() >= expiry) {
          logout();
        }
      };

      checkExpiry();
      intervalId = setInterval(checkExpiry, 60000);
    } catch (e) {
      console.error("Invalid token", e);
      logout();
    }

    return () => clearInterval(intervalId);
  }, [auth, logout]);

  const value = {
    auth,
    setAuth,
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
